// utils.ts - Utility Functions
import { Property, PaymentFrequency } from './types';
import { paymentFrequencies } from './data';

export const calculateTotal = (property: Property): number => {
  return property.monthlyRate + property.dues + property.penalty;
};

export const calculateAmountByFrequency = (property: Property, frequency: string): number => {
  const freq = paymentFrequencies.find(f => f.id === frequency);
  if (!freq) return property.monthlyRate;
  
  const baseAmount = property.monthlyRate * freq.multiplier;
  const discount = freq.discount ? (baseAmount * freq.discount / 100) : 0;
  return baseAmount - discount;
};

export const getFrequencyColor = (frequency: string): string => {
  switch (frequency) {
    case 'monthly': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'quarterly': return 'bg-green-50 text-green-700 border-green-200';
    case 'yearly': return 'bg-purple-50 text-purple-700 border-purple-200';
    default: return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

export const getStatusColor = (property: Property): string => {
  const total = calculateTotal(property);
  if (total <= property.monthlyRate) return 'bg-green-50 text-green-700 border-green-200';
  if (property.penalty === 0) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  return 'bg-red-50 text-red-700 border-red-200';
};

export const getStatus = (property: Property): string => {
  const total = calculateTotal(property);
  if (total <= property.monthlyRate) return 'Current';
  if (property.penalty === 0) return 'Due';
  return 'Overdue';
};

export const getVehicleStatusColor = (status: string): string => {
  switch (status) {
    case 'active': return 'bg-green-50 text-green-700 border-green-200';
    case 'maintenance': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'inactive': return 'bg-red-50 text-red-700 border-red-200';
    default: return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

export const getBinStatusColor = (status: string): string => {
  switch (status) {
    case 'empty': return 'bg-green-50 text-green-700 border-green-200';
    case 'partial': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'full': return 'bg-red-50 text-red-700 border-red-200';
    default: return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

export const getUserStatusColor = (status: string): string => {
  return status === 'active' 
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-50 text-gray-700 border-gray-200';
};

export const getFuelLevelColor = (level: number): string => {
  if (level >= 70) return 'text-green-600';
  if (level >= 30) return 'text-yellow-600';
  return 'text-red-600';
};

export const getConditionColor = (condition: string): string => {
  switch (condition) {
    case 'excellent': return 'text-green-600';
    case 'good': return 'text-blue-600';
    case 'maintenance': return 'text-yellow-600';
    case 'poor': return 'text-red-600';
    default: return 'text-gray-600';
  }
};

export const formatCurrency = (amount: number): string => {
  return `₹${amount.toLocaleString()}`;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
};

export const generateReceiptNumber = (): string => {
  return `GC${Date.now().toString().slice(-6)}`;
};

export const filterProperties = (properties: Property[], searchTerm: string, filterStatus: string) => {
  return properties.filter(property => {
    const matchesSearch = property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.plotNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.phone.includes(searchTerm);
    
    if (filterStatus === 'all') return matchesSearch;
    if (filterStatus === 'current') return matchesSearch && calculateTotal(property) <= property.monthlyRate;
    if (filterStatus === 'due') return matchesSearch && calculateTotal(property) > property.monthlyRate && property.penalty === 0;
    if (filterStatus === 'overdue') return matchesSearch && property.penalty > 0;
    
    return matchesSearch;
  });
};